import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { tomorrow as tomorrowTheme } from "react-syntax-highlighter/dist/esm/styles/prism";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import blogsData from "../../data/blogs.json";
import { PageLayout } from "../Layout";
import "./BlogDetails.css";

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  authorAvatar: string;
  publishDate: string;
  readTime: string;
  category: string;
  tags: string[];
  featuredImage: string;
  seo: {
    metaTitle: string;
    metaDescription: string;
    metaKeywords: string;
    ogTitle: string;
    ogDescription: string;
    ogImage: string;
    twitterCard: string;
    canonicalUrl: string;
  };
  settings: {
    showToc: boolean;
    enableComments: boolean;
    enableSocialSharing: boolean;
    enableReadingProgress: boolean;
    enableRelatedPosts: boolean;
  };
  analytics: {
    googleAnalytics: {
      enabled: boolean;
      trackingId: string;
    };
    facebookPixel: {
      enabled: boolean;
      pixelId: string;
    };
    hotjar: {
      enabled: boolean;
      siteId: string;
    };
  };
  relatedPosts: string[];
  stats: {
    views: number;
    likes: number;
    shares: number;
    comments: number;
  };
}

interface TableOfContentsItem {
  id: string;
  text: string;
  level: number;
}

const BlogDetails: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [blog, setBlog] = useState<BlogPost | null>(null);
  const [tableOfContents, setTableOfContents] = useState<TableOfContentsItem[]>(
    []
  );
  const [activeHeading, setActiveHeading] = useState<string>("");
  const [readingProgress, setReadingProgress] = useState(0);
  const [likes, setLikes] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const headingsRef = useRef<Map<string, IntersectionObserverEntry>>(new Map());

  useEffect(() => {
    // Find blog by slug
    const foundBlog = blogsData.blogs.find((b) => b.slug === slug);
    if (foundBlog) {
      setBlog(foundBlog);
      setLikes(foundBlog.stats.likes);

      // Extract headings for TOC
      if (foundBlog.settings.showToc) {
        const headings = extractHeadings(foundBlog.content);
        setTableOfContents(headings);
      }

      // Track page view
      trackPageView(foundBlog);
    }
  }, [slug]);

  useEffect(() => {
    if (!blog) return;

    // Set up intersection observer for headings
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          headingsRef.current.set(entry.target.id, entry);
        });

        // Find the most visible heading
        let maxVisible = 0;
        let mostVisibleHeading = "";

        headingsRef.current.forEach((entry, id) => {
          if (entry.isIntersecting) {
            const rect = entry.boundingClientRect;
            const visibleHeight =
              Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
            if (visibleHeight > maxVisible) {
              maxVisible = visibleHeight;
              mostVisibleHeading = id;
            }
          }
        });

        setActiveHeading(mostVisibleHeading);
      },
      {
        rootMargin: "-20% 0px -80% 0px",
        threshold: 0,
      }
    );

    // Observe all headings
    const headings = document.querySelectorAll("h1, h2, h3, h4, h5, h6");
    headings.forEach((heading) => {
      if (heading.id) {
        observer.observe(heading);
      }
    });

    return () => observer.disconnect();
  }, [blog]);

  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;

      const element = contentRef.current;
      const totalHeight = element.scrollHeight - element.clientHeight;
      const scrollProgress = (window.scrollY / totalHeight) * 100;
      setReadingProgress(Math.min(scrollProgress, 100));
    };

    if (blog?.settings.enableReadingProgress) {
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, [blog]);

  const extractHeadings = (content: string): TableOfContentsItem[] => {
    const headingRegex = /^(#{1,6})\s+(.+)$/gm;
    const headings: TableOfContentsItem[] = [];
    let match;

    while ((match = headingRegex.exec(content)) !== null) {
      const level = match[1].length;
      const text = match[2].trim();
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-");

      headings.push({ id, text, level });
    }

    return headings;
  };

  const trackPageView = (blogPost: BlogPost) => {
    // Google Analytics
    if (blogPost.analytics.googleAnalytics.enabled && window.gtag) {
      window.gtag("config", blogPost.analytics.googleAnalytics.trackingId, {
        page_title: blogPost.seo.metaTitle,
        page_location: blogPost.seo.canonicalUrl,
      });
    }

    // Facebook Pixel
    if (blogPost.analytics.facebookPixel.enabled && window.fbq) {
      window.fbq("track", "PageView");
    }

    // Hotjar
    if (blogPost.analytics.hotjar.enabled && window.hj) {
      window.hj("stateChange", blogPost.seo.canonicalUrl);
    }
  };

  const handleLike = () => {
    if (!blog) return;

    if (!isLiked) {
      setLikes((prev) => prev + 1);
      setIsLiked(true);

      // Track like event
      if (blog.analytics.googleAnalytics.enabled && window.gtag) {
        window.gtag("event", "like", {
          event_category: "blog",
          event_label: blog.slug,
        });
      }
    }
  };

  const handleShare = (platform: string) => {
    if (!blog) return;

    const url = encodeURIComponent(blog.seo.canonicalUrl);
    const title = encodeURIComponent(blog.title);
    const text = encodeURIComponent(blog.excerpt);

    let shareUrl = "";
    switch (platform) {
      case "twitter":
        shareUrl = `https://twitter.com/intent/tweet?url=${url}&text=${title}`;
        break;
      case "facebook":
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case "linkedin":
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
        break;
      case "whatsapp":
        shareUrl = `https://wa.me/?text=${title}%20${url}`;
        break;
    }

    if (shareUrl) {
      window.open(shareUrl, "_blank", "width=600,height=400");

      // Track share event
      if (blog.analytics.googleAnalytics.enabled && window.gtag) {
        window.gtag("event", "share", {
          method: platform,
          content_type: "blog",
          item_id: blog.slug,
        });
      }
    }
  };

  const scrollToHeading = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (!blog) {
    return (
      <PageLayout title="Blog Post Not Found" showBreadcrumb={false}>
        <div className="blog-details-container">
          <div className="blog-not-found">
            <h1>Blog Post Not Found</h1>
            <p>The blog post you're looking for doesn't exist.</p>
            <Link to="/blog" className="back-to-blog-btn">
              Back to Blog
            </Link>
          </div>
        </div>
      </PageLayout>
    );
  }

  const relatedPosts = blogsData.blogs.filter((b) =>
    blog.relatedPosts.includes(b.id)
  );

  return (
    <PageLayout title={blog.title} subtitle={blog.excerpt}>
      <Helmet>
        <title>{blog.seo.metaTitle}</title>
        <meta name="description" content={blog.seo.metaDescription} />
        <meta name="keywords" content={blog.seo.metaKeywords} />

        {/* Open Graph */}
        <meta property="og:title" content={blog.seo.ogTitle} />
        <meta property="og:description" content={blog.seo.ogDescription} />
        <meta property="og:image" content={blog.seo.ogImage} />
        <meta property="og:url" content={blog.seo.canonicalUrl} />
        <meta property="og:type" content="article" />

        {/* Twitter Card */}
        <meta name="twitter:card" content={blog.seo.twitterCard} />
        <meta name="twitter:title" content={blog.seo.ogTitle} />
        <meta name="twitter:description" content={blog.seo.ogDescription} />
        <meta name="twitter:image" content={blog.seo.ogImage} />

        {/* Canonical URL */}
        <link rel="canonical" href={blog.seo.canonicalUrl} />

        {/* Article structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: blog.title,
            description: blog.excerpt,
            image: blog.featuredImage,
            author: {
              "@type": "Person",
              name: blog.author,
              image: blog.authorAvatar,
            },
            publisher: {
              "@type": "Organization",
              name: "Imamul Hassan",
              logo: {
                "@type": "ImageObject",
                url: "/images/profile-photo.jpg",
              },
            },
            datePublished: blog.publishDate,
            dateModified: blog.publishDate,
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": blog.seo.canonicalUrl,
            },
          })}
        </script>
      </Helmet>

      <div className="blog-details-container">
        {/* Reading Progress Bar */}
        {blog.settings.enableReadingProgress && (
          <div className="reading-progress-bar">
            <div
              className="reading-progress-fill"
              style={{ width: `${readingProgress}%` }}
            />
          </div>
        )}

        <div className="blog-content-wrapper">
          {/* Main Content */}
          <main className="blog-main-content" ref={contentRef}>
            {/* Header */}
            <header className="blog-header">
              <div className="blog-meta">
                <span className="blog-category">{blog.category}</span>
                <span className="blog-date">
                  {formatDate(blog.publishDate)}
                </span>
                <span className="blog-read-time">{blog.readTime}</span>
              </div>

              <h1 className="blog-title">{blog.title}</h1>
              <p className="blog-excerpt">{blog.excerpt}</p>

              <div className="blog-author">
                <img
                  src={blog.authorAvatar}
                  alt={blog.author}
                  className="author-avatar"
                />
                <div className="author-info">
                  <span className="author-name">{blog.author}</span>
                  <span className="author-title">Full Stack Developer</span>
                </div>
              </div>
            </header>

            {/* Featured Image */}
            {blog.featuredImage && (
              <div className="blog-featured-image">
                <img src={blog.featuredImage} alt={blog.title} loading="lazy" />
              </div>
            )}

            {/* Blog Content */}
            <article className="blog-content">
              <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkMath]}
                rehypePlugins={[rehypeKatex]}
                components={{
                  h1: ({ children, ...props }) => (
                    <h1
                      id={children
                        ?.toString()
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")}
                      {...props}
                    >
                      {children}
                    </h1>
                  ),
                  h2: ({ children, ...props }) => (
                    <h2
                      id={children
                        ?.toString()
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")}
                      {...props}
                    >
                      {children}
                    </h2>
                  ),
                  h3: ({ children, ...props }) => (
                    <h3
                      id={children
                        ?.toString()
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")}
                      {...props}
                    >
                      {children}
                    </h3>
                  ),
                  h4: ({ children, ...props }) => (
                    <h4
                      id={children
                        ?.toString()
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")}
                      {...props}
                    >
                      {children}
                    </h4>
                  ),
                  h5: ({ children, ...props }) => (
                    <h5
                      id={children
                        ?.toString()
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")}
                      {...props}
                    >
                      {children}
                    </h5>
                  ),
                  h6: ({ children, ...props }) => (
                    <h6
                      id={children
                        ?.toString()
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")}
                      {...props}
                    >
                      {children}
                    </h6>
                  ),
                  code: ({ node, className, children, ...props }) => {
                    const match = /language-(\w+)/.exec(className || "");
                    return match ? (
                      <SyntaxHighlighter
                        style={tomorrowTheme}
                        language={match[1]}
                        PreTag="div"
                      >
                        {String(children).replace(/\n$/, "")}
                      </SyntaxHighlighter>
                    ) : (
                      <code className={className} {...props}>
                        {children}
                      </code>
                    );
                  },
                  blockquote: ({ children, ...props }) => (
                    <blockquote className="blog-quote" {...props}>
                      {children}
                    </blockquote>
                  ),
                  img: ({ src, alt, ...props }) => (
                    <img
                      src={src}
                      alt={alt}
                      className="blog-image"
                      loading="lazy"
                      {...props}
                    />
                  ),
                  a: ({ href, children, ...props }) => (
                    <a
                      href={href}
                      className="blog-link"
                      target="_blank"
                      rel="noopener noreferrer"
                      {...props}
                    >
                      {children}
                    </a>
                  ),
                  table: ({ children, ...props }) => (
                    <div className="table-wrapper">
                      <table className="blog-table" {...props}>
                        {children}
                      </table>
                    </div>
                  ),
                }}
              >
                {blog.content}
              </ReactMarkdown>
            </article>

            {/* Tags */}
            <div className="blog-tags">
              {blog.tags.map((tag) => (
                <span key={tag} className="blog-tag">
                  {tag}
                </span>
              ))}
            </div>

            {/* Engagement Section */}
            <div className="blog-engagement">
              <div className="engagement-stats">
                <div className="stat-item">
                  <span className="stat-number">{blog.stats.views}</span>
                  <span className="stat-label">Views</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">{likes}</span>
                  <span className="stat-label">Likes</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">{blog.stats.shares}</span>
                  <span className="stat-label">Shares</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">{blog.stats.comments}</span>
                  <span className="stat-label">Comments</span>
                </div>
              </div>

              <div className="engagement-actions">
                <button
                  className={`like-btn ${isLiked ? "liked" : ""}`}
                  onClick={handleLike}
                >
                  <span className="like-icon">❤️</span>
                  {isLiked ? "Liked" : "Like"}
                </button>

                {blog.settings.enableSocialSharing && (
                  <div className="share-buttons">
                    <button
                      className="share-btn twitter"
                      onClick={() => handleShare("twitter")}
                      title="Share on Twitter"
                    >
                      🐦
                    </button>
                    <button
                      className="share-btn facebook"
                      onClick={() => handleShare("facebook")}
                      title="Share on Facebook"
                    >
                      📘
                    </button>
                    <button
                      className="share-btn linkedin"
                      onClick={() => handleShare("linkedin")}
                      title="Share on LinkedIn"
                    >
                      💼
                    </button>
                    <button
                      className="share-btn whatsapp"
                      onClick={() => handleShare("whatsapp")}
                      title="Share on WhatsApp"
                    >
                      📱
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Related Posts */}
            {blog.settings.enableRelatedPosts && relatedPosts.length > 0 && (
              <section className="related-posts">
                <h2>Related Posts</h2>
                <div className="related-posts-grid">
                  {relatedPosts.map((post) => (
                    <Link
                      key={post.id}
                      to={`/blog/${post.slug}`}
                      className="related-post-card"
                    >
                      {post.featuredImage && (
                        <img
                          src={post.featuredImage}
                          alt={post.title}
                          className="related-post-image"
                        />
                      )}
                      <div className="related-post-content">
                        <h3>{post.title}</h3>
                        <p>{post.excerpt}</p>
                        <div className="related-post-meta">
                          <span>{formatDate(post.publishDate)}</span>
                          <span>{post.readTime}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Comments Section */}
            {blog.settings.enableComments && (
              <section className="comments-section">
                <h2>Comments</h2>
                <div className="comments-placeholder">
                  <p>Comments feature coming soon! 🚀</p>
                  <p>
                    You can share your thoughts on social media or reach out
                    directly.
                  </p>
                </div>
              </section>
            )}
          </main>

          {/* Sidebar */}
          <aside className="blog-sidebar">
            {/* Table of Contents */}
            {blog.settings.showToc && tableOfContents.length > 0 && (
              <div className="toc-container">
                <h3>Table of Contents</h3>
                <nav className="toc-nav">
                  {tableOfContents.map((item) => (
                    <button
                      key={item.id}
                      className={`toc-item toc-level-${item.level} ${
                        activeHeading === item.id ? "active" : ""
                      }`}
                      onClick={() => scrollToHeading(item.id)}
                    >
                      {item.text}
                    </button>
                  ))}
                </nav>
              </div>
            )}

            {/* Author Card */}
            <div className="author-card">
              <img
                src={blog.authorAvatar}
                alt={blog.author}
                className="author-card-avatar"
              />
              <h3>{blog.author}</h3>
              <p>
                Full Stack Developer passionate about creating innovative web
                solutions.
              </p>
              <div className="author-social">
                <a
                  href="https://github.com/imamulhassan"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  GitHub
                </a>
                <a
                  href="https://linkedin.com/in/imamulhassan"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  LinkedIn
                </a>
                <a
                  href="https://twitter.com/imamulhassan"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Twitter
                </a>
              </div>
            </div>

            {/* Newsletter Signup */}
            <div className="newsletter-signup">
              <h3>Stay Updated</h3>
              <p>
                Get the latest articles and insights delivered to your inbox.
              </p>
              <form className="newsletter-form">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="newsletter-input"
                />
                <button type="submit" className="newsletter-btn">
                  Subscribe
                </button>
              </form>
            </div>
          </aside>
        </div>
      </div>
    </PageLayout>
  );
};

export default BlogDetails;
